import React, { Suspense, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { AnimatedResident } from '../components/AnimatedResident';
import SafeMount from '../components/SafeMount';
import { player, TP_TEST_BODY } from './player';
import { WALK_SPEED } from './walkBody';
import { useDriveStore } from './drive/driveStore';
import { camBasis } from '../services/worldObjects';

// ── YOUR OWN BODY ────────────────────────────────────────────────────────────
//
// In third person the camera stands behind you, so there has to be a you: the
// same rigged avatar other players already see (Peers draws it from the same
// roster entry), walking when you walk, idling when you stop, turned to face
// where you are going rather than where the camera points — so you can look
// around a corner without your body spinning to follow.
//
// Only for a player who HAS a body (useHasBody — a member whose artist has a
// rigged model). A guest stays first person; earning the body is part of the
// come-up, not something this hands out.

export default function PlayerBody() {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const npcList = useWorldStore((s) => s.npcList);
  const driving = useDriveStore((s) => s.driving) != null;
  const want = TP_TEST_BODY ?? myAvatarId;
  const rig = useMemo(
    () => (want ? (npcList || []).find((n) => String(n.avatarId) === String(want) && n.rigged) : null),
    [npcList, want],
  );
  const group = useRef();
  const yaw = useRef(0);
  const [moving, setMoving] = useState(false);
  const [rate, setRate] = useState(1);

  useFrame((_, dt) => {
    const g = group.current;
    if (!g) return;
    const show = player.third && !driving;
    // Appearing (third person switched on, or out of a car): face where the
    // camera is looking, not the model's default +z — which is straight at it.
    if (show && !g.visible) yaw.current = Math.atan2(camBasis.fx, camBasis.fz);
    g.visible = show;
    if (!show) return;
    g.position.set(player.x, player.jump, player.z);
    player.height = rig?.height || 0.85;   // the chase camera frames THIS body
    const sp = Math.hypot(player.vx, player.vz);
    if (sp > 0.3) {
      // Turn toward the direction of travel, the short way round.
      const target = Math.atan2(player.vx, player.vz);
      let d = target - yaw.current;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      yaw.current += d * Math.min(1, dt * 12);
    }
    g.rotation.y = yaw.current;
    const m = sp > 0.5;
    if (m !== moving) setMoving(m);
    // The walk clip was authored at a stroll; speed it up with the body so the
    // feet keep up (stepped, so React only hears about real changes).
    const r = m ? Math.max(1, Math.round((sp / WALK_SPEED) * 1.6 * 4) / 4) : 1;
    if (r !== rate) setRate(r);
  });

  if (!rig) return null;
  return (
    <group ref={group} visible={false}>
      <SafeMount
        label={`player body ${rig.avatarId}`}
        retryMs={30000}
        onRetry={() => useGLTF.clear([rig.rigged, rig.walk, rig.idle].filter(Boolean))}
      >
        <Suspense fallback={null}>
          <AnimatedResident
            meshUrl={rig.rigged}
            rigPose={rig.pose || null}
            clipMap={rig.clips || null}
            clipUrls={[rig.walk, rig.idle].filter(Boolean)}
            play={moving ? 'walk' : 'idle'}
            timeScale={rate}
            height={rig.height || 1.8}
            position={[0, 0.02, 0]}
          />
        </Suspense>
      </SafeMount>
    </group>
  );
}
